import { useState, useRef, useEffect } from "react";
import { useScroll, motion, AnimatePresence } from "framer-motion";
import backgroundsquareInstall from "../../../assets/backgroundsquares3-Install.svg";
import step1Img from "../../../assets/step1img.webp";
import step2Img from "../../../assets/step2img.webp";
import step3Img from "../../../assets/step3img.webp";

function InstallSection() {
    const [StepCount, setStepCount] = useState(0);
    const containerRef = useRef(null);
    const StepImagesList = [step1Img, step2Img, step3Img];

    const textVariants = {
        hidden: { opacity: 0, y: 30 },
        show: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.65,
                ease: [0.16, 0.77, 0.47, 0.97], // Custom bezier for smooth movement
            },
        },
    };

    // Variants for the 'Download' button, now only fades in
    const buttonVariants = {
        hidden: { opacity: 0 },
        show: {
            opacity: 1,
            transition: {
                duration: 0.65,
            },
        },
    };

    // Variants for the images
    const imageVariants = {
        hidden: { opacity: 0, scale: 0.92 },
        show: {
            opacity: 1,
            scale: 1,
            transition: {
                duration: 0.7,
                ease: "backOut",
            },
        },
    };

    // New variant for each individual step container, which staggers its own children
    const stepContainerVariants = {
        hidden: { opacity: 0 },
        show: {
            opacity: 1,
            transition: {
                staggerChildren: 0.25, // Staggers the animation of text and image within this step
                delayChildren: 0.15,
            },
        },
    };

    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start start", "end end"],
    });

    useEffect(() => {
        const unsubscribe = scrollYProgress.on("change", (latest) => {
            const step = Math.floor(latest * 3);
            setStepCount(Math.min(step, 2));
        });
        return () => unsubscribe();
    }, [scrollYProgress]);

    return (
        <>
            {window.matchMedia("(max-width: 430px)").matches ? (
                <section className="installSectionContainer-mobile">
                    <div className="InstallDescription">
                        <div className="install-section-wrapper">
                            {/* Step 1 */}
                            <motion.div
                                className="stepMobileWrapper"
                                initial="hidden"
                                whileInView="show"
                                viewport={{ once: true, margin: "-20%" }}
                                variants={stepContainerVariants}
                            >
                                <header className="stepMobileText">
                                    <motion.h3
                                        className="text-title"
                                        variants={textVariants}
                                    >
                                        Install Oakmate in Seconds
                                    </motion.h3>
                                    <motion.p
                                        className="text-subtitle"
                                        variants={textVariants}
                                    >
                                        Available for Chrome, Firefox, and
                                        Safari—quick one-click setup, no
                                        technical skills needed
                                    </motion.p>
                                    <motion.a
                                        className="download-cta-mobile"
                                        href="https://chromewebstore.google.com/detail/oakmate-game-review/fdledmdlgggnfddaeillbpahblnblbne"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        variants={buttonVariants}
                                    >
                                        <span className="download-cta-text">
                                            Add To Chrome - It's free
                                        </span>
                                        <svg
                                            width="24"
                                            height="24"
                                            fill="none"
                                            viewBox="0 0 25 24"
                                            className="download-cta-icon"
                                        >
                                            <g>
                                                <path
                                                    fill="#E94737"
                                                    d="M12.405 1.969a9.935 9.935 0 0 0-9.936 9.936 9.935 9.935 0 0 0 9.936 9.936 9.935 9.935 0 0 0 9.936-9.936 9.935 9.935 0 0 0-9.936-9.936Z"
                                                ></path>
                                                <path
                                                    fill="#30A04C"
                                                    d="m3.697 16.693.198.346a9.927 9.927 0 0 0 8.51 4.802c.09 0 .18 0 .272-.002l-.158-.277L16.9 19.95V14.27H8.308l-4.37-7.568a9.89 9.89 0 0 0-1.47 5.202c0 1.737.446 3.369 1.229 4.788Z"
                                                ></path>
                                                <path
                                                    fill="#FBBC1C"
                                                    d="M21.158 7.2v-.028h-.014a9.307 9.307 0 0 0-.272-.47l-.272.47h-8.195v7.098h4.098l-4.37 7.57a9.937 9.937 0 0 0 8.876-4.968 9.918 9.918 0 0 0 .15-9.671Z"
                                                ></path>
                                                <path
                                                    fill="#E94737"
                                                    d="M21.01 6.937a9.931 9.931 0 0 0-8.434-4.968h-.357A9.87 9.87 0 0 0 7.438 3.3a9.88 9.88 0 0 0-3.504 3.406l.27.466h16.938a11.704 11.704 0 0 0-.132-.236Z"
                                                ></path>
                                                <path
                                                    fill="#FEFEFD"
                                                    d="M12.405 7.173a4.73 4.73 0 0 0-4.732 4.732 4.73 4.73 0 0 0 4.732 4.732 4.73 4.73 0 0 0 4.732-4.732 4.73 4.73 0 0 0-4.732-4.732Z"
                                                ></path>
                                                <path
                                                    fill="#3884F0"
                                                    d="M12.405 8.12a3.784 3.784 0 0 0-3.785 3.785 3.784 3.784 0 0 0 3.785 3.785 3.784 3.784 0 0 0 3.785-3.785 3.784 3.784 0 0 0-3.785-3.785Z"
                                                ></path>
                                            </g>
                                        </svg>
                                    </motion.a>
                                </header>
                                <motion.img
                                    src={step1Img}
                                    alt="Step 1 Illustration"
                                    className="stepImg"
                                    variants={imageVariants}
                                />
                            </motion.div>
                            {/* Step 2 */}
                            <motion.div
                                className="stepMobileWrapper"
                                initial="hidden"
                                whileInView="show"
                                viewport={{ once: true, margin: "-20%" }}
                                variants={stepContainerVariants}
                            >
                                <header className="stepMobileText">
                                    <motion.h3
                                        className="text-title"
                                        variants={textVariants}
                                    >
                                        Launch Your Analysis Suite
                                    </motion.h3>
                                    <motion.p
                                        className="text-subtitle"
                                        variants={textVariants}
                                    >
                                        After any Chess.com or Lichess game,
                                        click the site's "Game Review" button or
                                        the Oakmate icon
                                    </motion.p>
                                </header>
                                <motion.img
                                    src={step2Img}
                                    alt="Step 2 Illustration"
                                    className="stepImg"
                                    variants={imageVariants}
                                />
                            </motion.div>
                            {/* Step 3 */}
                            <motion.div
                                className="stepMobileWrapper"
                                initial="hidden"
                                whileInView="show"
                                viewport={{ once: true, margin: "-20%" }}
                                variants={stepContainerVariants}
                            >
                                <header className="stepMobileText">
                                    <motion.h3
                                        className="text-title"
                                        variants={textVariants}
                                    >
                                        Master Like a Pro
                                    </motion.h3>
                                    <motion.p
                                        className="text-subtitle"
                                        variants={textVariants}
                                    >
                                        Unlock instant move evaluations,
                                        accuracy breakdowns, game ratings and
                                        more — Yours forever, updates included
                                    </motion.p>
                                </header>
                                <motion.img
                                    src={step3Img}
                                    alt="Step 3 Illustration"
                                    className="stepImg"
                                    variants={imageVariants}
                                />
                            </motion.div>
                        </div>
                    </div>
                    <div className="backgroundsquareInstallMobile-wrapper">
                        <img
                            src={backgroundsquareInstall}
                            alt="Background"
                            className="backgroundsquareInstallMobile"
                        />
                    </div>
                </section>
            ) : (
                <motion.section
                    className="containersuperwrapper"
                    ref={containerRef}
                >
                    <div className="descriptionSection-Container">
                        <img
                            src={backgroundsquareInstall}
                            alt="Background"
                            className="backgroundsquareInstall"
                        />
                        <div className="InstallDescription">
                            <div className="install-section-wrapper">
                                <div className="progressbar-wrapper">
                                    <svg
                                        width="13"
                                        height="108"
                                        viewBox="0 0 13 108"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                        alt="Steps Progress Bar"
                                        className="stepsProgressBar"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M12.9997 6.50577C12.9997 2.91273 10.0896 0 6.49986 0C2.91008 0 0 2.91273 0 6.50577C0 9.80467 2.45312 12.5301 5.63312 12.9542V47.0297C2.45326 47.4539 0.000279474 50.1793 0.000279474 53.4781C0.000279474 56.777 2.45341 59.5024 5.63341 59.9265V95.0458C2.45341 95.4699 0.000286436 98.1953 0.000286436 101.494C0.000286436 105.087 2.91037 108 6.50014 108C10.0899 108 13 105.087 13 101.494C13 98.1953 10.5468 95.4698 7.3667 95.0458V59.9265C10.5468 59.5025 13 56.777 13 53.4781C13 50.179 10.5466 47.4535 7.36642 47.0296V12.9542C10.5465 12.5302 12.9997 9.80473 12.9997 6.50577ZM6.5 11C8.98528 11 11 8.98528 11 6.5C11 4.01472 8.98528 2 6.5 2C4.01472 2 2 4.01472 2 6.5C2 8.98528 4.01472 11 6.5 11ZM6.5 58C8.98528 58 11 55.9853 11 53.5C11 51.0147 8.98528 49 6.5 49C4.01472 49 2 51.0147 2 53.5C2 55.9853 4.01472 58 6.5 58ZM6.5 106C8.98528 106 11 103.985 11 101.5C11 99.0147 8.98528 97 6.5 97C4.01472 97 2 99.0147 2 101.5C2 103.985 4.01472 106 6.5 106Z"
                                            fill="var(--text)"
                                        />
                                        <path
                                            d="M11 6.5C11 8.98528 8.98528 11 6.5 11C4.01472 11 2 8.98528 2 6.5C2 4.01472 4.01472 2 6.5 2C8.98528 2 11 4.01472 11 6.5Z"
                                            fill={
                                                StepCount === 0
                                                    ? "var(--primaryPurple)"
                                                    : "transparent"
                                            }
                                        />
                                        <path
                                            d="M11 53.5C11 55.9853 8.98528 58 6.5 58C4.01472 58 2 55.9853 2 53.5C2 51.0147 4.01472 49 6.5 49C8.98528 49 11 51.0147 11 53.5Z"
                                            fill={
                                                StepCount === 1
                                                    ? "var(--primaryPurple)"
                                                    : "transparent"
                                            }
                                        />

                                        <path
                                            d="M11 101.5C11 103.985 8.98528 106 6.5 106C4.01472 106 2 103.985 2 101.5C2 99.0147 4.01472 97 6.5 97C8.98528 97 11 99.0147 11 101.5Z"
                                            fill={
                                                StepCount === 2
                                                    ? "var(--primaryPurple)"
                                                    : "transparent"
                                            }
                                        />
                                    </svg>
                                </div>
                                <div className="maintext-container">
                                    <AnimatePresence mode="wait">
                                        {StepCount === 0 && (
                                            <motion.header
                                                key="step0"
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: -20 }}
                                                transition={{ duration: 0.3 }}
                                            >
                                                <h3 className="text-title">
                                                    Install Oakmate in Seconds
                                                </h3>
                                                <p className="text-subtitle">
                                                    Available for Chrome,
                                                    Firefox, and Safari—quick
                                                    one-click setup, no
                                                    technical skills needed
                                                </p>
                                                <a
                                                    className="download-cta-mobile"
                                                    href="https://chromewebstore.google.com/detail/oakmate-game-review/fdledmdlgggnfddaeillbpahblnblbne"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    <span className="download-cta-text">
                                                        Add To Chrome - It's
                                                        free
                                                    </span>
                                                    <svg
                                                        width="24"
                                                        height="24"
                                                        fill="none"
                                                        viewBox="0 0 25 24"
                                                        className="download-cta-icon"
                                                    >
                                                        <g>
                                                            <path
                                                                fill="#E94737"
                                                                d="M12.405 1.969a9.935 9.935 0 0 0-9.936 9.936 9.935 9.935 0 0 0 9.936 9.936 9.935 9.935 0 0 0 9.936-9.936 9.935 9.935 0 0 0-9.936-9.936Z"
                                                            ></path>
                                                            <path
                                                                fill="#30A04C"
                                                                d="m3.697 16.693.198.346a9.927 9.927 0 0 0 8.51 4.802c.09 0 .18 0 .272-.002l-.158-.277L16.9 19.95V14.27H8.308l-4.37-7.568a9.89 9.89 0 0 0-1.47 5.202c0 1.737.446 3.369 1.229 4.788Z"
                                                            ></path>
                                                            <path
                                                                fill="#FBBC1C"
                                                                d="M21.158 7.2v-.028h-.014a9.307 9.307 0 0 0-.272-.47l-.272.47h-8.195v7.098h4.098l-4.37 7.57a9.937 9.937 0 0 0 8.876-4.968 9.918 9.918 0 0 0 .15-9.671Z"
                                                            ></path>
                                                            <path
                                                                fill="#E94737"
                                                                d="M21.01 6.937a9.931 9.931 0 0 0-8.434-4.968h-.357A9.87 9.87 0 0 0 7.438 3.3a9.88 9.88 0 0 0-3.504 3.406l.27.466h16.938a11.704 11.704 0 0 0-.132-.236Z"
                                                            ></path>
                                                            <path
                                                                fill="#FEFEFD"
                                                                d="M12.405 7.173a4.73 4.73 0 0 0-4.732 4.732 4.73 4.73 0 0 0 4.732 4.732 4.73 4.73 0 0 0 4.732-4.732 4.73 4.73 0 0 0-4.732-4.732Z"
                                                            ></path>
                                                            <path
                                                                fill="#3884F0"
                                                                d="M12.405 8.12a3.784 3.784 0 0 0-3.785 3.785 3.784 3.784 0 0 0 3.785 3.785 3.784 3.784 0 0 0 3.785-3.785 3.784 3.784 0 0 0-3.785-3.785Z"
                                                            ></path>
                                                        </g>
                                                    </svg>
                                                </a>
                                            </motion.header>
                                        )}
                                        {StepCount === 1 && (
                                            <motion.header
                                                key="step1"
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: -20 }}
                                                transition={{ duration: 0.3 }}
                                            >
                                                <h3 className="text-title">
                                                    Launch Your Analysis Suite
                                                </h3>
                                                <p className="text-subtitle">
                                                    After any Chess.com or
                                                    Lichess game, click the
                                                    site's “Game Review” button
                                                    or the Oakmate icon
                                                </p>
                                            </motion.header>
                                        )}
                                        {StepCount === 2 && (
                                            <motion.header
                                                key="step2"
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: -20 }}
                                                transition={{ duration: 0.3 }}
                                            >
                                                <h3 className="text-title">
                                                    Master Like a Pro
                                                </h3>
                                                <p className="text-subtitle">
                                                    Unlock instant move
                                                    evaluations, accuracy
                                                    breakdowns, game ratings and
                                                    more — Yours forever,
                                                    updates included
                                                </p>
                                            </motion.header>
                                        )}
                                    </AnimatePresence>
                                </div>
                                <div className="mainillustration-wrapper">
                                    <AnimatePresence mode="wait">
                                        <motion.img
                                            key={StepCount}
                                            src={StepImagesList[StepCount]}
                                            alt="Steps Illustration"
                                            className="stepImg"
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -20 }}
                                            transition={{ duration: 0.3 }}
                                        />
                                    </AnimatePresence>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.section>
            )}
        </>
    );
}

export default InstallSection;
